import { Component, DestroyRef, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';

import { CollapseModule } from 'ngx-bootstrap/collapse';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaskDirective } from 'ngx-mask';
import { NgClass } from '@angular/common';
// Third Party
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { calculateFFLExpirationDate } from 'src/app/shared/functions';
import {
  Contact,
  Customer,
  Vendor,
  EditCustomerResponsePackage,
  Title,
  salesTax,
  EditVendorResponsePackage,
} from 'src/app/shared/models/accounting';
import {
  ContactTypes,
  ModalID,
  ModalType,
  FFLMonthCharacters,
} from 'src/app/shared/models/constants';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import { AccountingService } from '../../services/accounting.service';
import { FFL } from 'src/app/shared/models/general';

@Component({
  selector: 'app-add-contact',
  templateUrl: './add-edit-contact.component.html',
  styleUrls: ['./add-edit-contact.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CollapseModule, NgSelectModule, NgxMaskDirective, NgClass],
})
export class AddEditContactComponent {
  private destroyRef = inject(DestroyRef);
  add_edit_contact_form: FormGroup;
  customer: Customer;
  vendor: Vendor;
  modal_type: string;
  rowIndex: number;
  contact_type: string;

  titles = Title;
  customerDisplayNames: string[] = [];
  distinct_cities: string[] = [];

  /**
   * Handles manual city entry from ng-select addTag event.
   * Accepts either a string or an event/object, extracts the city name, and adds it if not present.
   */
  onCityAddTag(event: any) {
    let newCity: string = '';
    if (typeof event === 'string') {
      newCity = event;
    } else if (event && typeof event === 'object') {
      newCity = event.name || event.label || '';
    }
    if (newCity && !this.distinct_cities.includes(newCity)) {
      this.distinct_cities = [...this.distinct_cities, newCity];
    }
    this.add_edit_contact_form.patchValue({ city: newCity });
  }
  sales_tax_options: string[] = [];

  /** Friendly labels for every form control that may be required. The set
   * varies with contact_type (Customer adds tax_option) and FFL state
   * (Vendor with a valid FFL adds legal_name + ffl_email). Listing them all
   * here is harmless — controls that aren't required just won't be invalid. */
  private static readonly REQUIRED_FIELD_LABELS: Record<string, string> = {
    display_name: 'Display Name',
    tax_option: 'Tax Option',
    legal_name: 'Legal Name',
    ffl_email: 'FFL Email',
  };

  /** Names of required fields that aren't yet satisfied. Empty array hides
   * the alert. Rendered as chips in the modal header. */
  missing_fields: string[] = [];

  nameContactIsCollapsed;
  addressIsCollapsed;
  otherIsCollapsed;
  fflIsCollapsed;
  addFfl: boolean = true;

  // Modals
  modalRef: BsModalRef;
  modalConfig = { animated: true };

  constructor(
    protected element: ElementRef,
    private modalService: BsModalService,
    private fb: FormBuilder,
    public sharedService: SharedService,
    public accountingService: AccountingService,
  ) {
    // Initializing Form
    this.add_edit_contact_form = this.fb.group({
      name_title: new FormControl({ value: null, disabled: false }),
      name_first: new FormControl({ value: null, disabled: false }),
      name_middle: new FormControl({ value: null, disabled: false }),
      name_last: new FormControl({ value: null, disabled: false }),
      name_suffix: new FormControl({ value: null, disabled: false }),
      company_name: new FormControl({ value: null, disabled: false }),
      display_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      email: new FormControl({ value: null, disabled: false }),
      phone_number: new FormControl({ value: null, disabled: false }),
      fax_number: new FormControl({ value: null, disabled: false }),
      website: new FormControl({ value: null, disabled: false }),
      name_to_print_on_checks: new FormControl({
        value: null,
        disabled: false,
      }),
      other: new FormControl({ value: null, disabled: false }),

      street: new FormControl({ value: null, disabled: false }),
      street2: new FormControl({ value: null, disabled: false }),
      city: new FormControl({ value: null, disabled: false }),
      state: new FormControl({ value: null, disabled: false }),
      zip_code: new FormControl({ value: null, disabled: false }, [
        // Allows partial input while typing: empty, 1-4 digits, 5 digits,
        // 5 digits + dash, or full 12345-6789. Dash only after exactly 5 digits.
        Validators.pattern(/^(\d{0,4}|\d{5}(-\d{0,4})?)$/),
      ]),
      country: new FormControl({ value: null, disabled: false }),

      memo: new FormControl({ value: null, disabled: false }),
    });
  }

  ngOnInit(): void {
    this.addDynamicFormControls();
    if (this.modal_type === ModalType.edit || (this.modal_type === ModalType.add && (this.customer || this.vendor))) {
      if (this.contact_type === ContactTypes.customer) {
        this.add_edit_contact_form.patchValue({
          name_title: this.customer?.contact?.name_title,
          name_first: this.customer?.contact?.name_first,
          name_middle: this.customer?.contact?.name_middle,
          name_last: this.customer?.contact?.name_last,
          name_suffix: this.customer?.contact?.name_suffix,
          company_name: this.customer?.contact?.company_name,
          display_name: this.customer?.contact?.display_name,
          email: this.customer?.contact?.email,
          phone_number: this.customer?.contact?.phone_number,
          fax_number: this.customer?.contact?.fax_number,
          website: this.customer?.contact?.website,
          name_to_print_on_checks:
            this.customer?.contact?.name_to_print_on_checks,
          other: this.customer?.contact?.other,
          street: this.customer?.address?.street,
          street2: this.customer?.address?.street2,
          city: this.customer?.address?.city,
          state: this.customer?.address?.state,
          zip_code: this.customer?.address?.zip_code,
          country: this.customer?.address?.country,
          tax_option: this.customer?.tax_option?.name,
          memo: this.customer?.memo,
        });
      } else {
        this.add_edit_contact_form.patchValue({
          name_title: this.vendor?.contact?.name_title,
          name_first: this.vendor?.contact?.name_first,
          name_middle: this.vendor?.contact?.name_middle,
          name_last: this.vendor?.contact?.name_last,
          name_suffix: this.vendor?.contact?.name_suffix,
          company_name: this.vendor?.contact?.company_name,
          display_name: this.vendor?.contact?.display_name,
          email: this.vendor?.contact?.email,
          phone_number: this.vendor?.contact?.phone_number,
          fax_number: this.vendor?.contact?.fax_number,
          website: this.vendor?.contact?.website,
          name_to_print_on_checks: this.vendor?.contact?.name_to_print_on_checks,
          other: this.vendor?.contact?.other,
          street: this.vendor?.address?.street,
          street2: this.vendor?.address?.street2,
          city: this.vendor?.address?.city,
          state: this.vendor?.address?.state,
          zip_code: this.vendor?.address?.zip_code,
          country: this.vendor?.address?.country,
          memo: this.vendor?.memo,
          ffl: this.vendor?.ffl?.ffl,
          legal_name: this.vendor?.ffl?.legal_name,
          business_name: this.vendor?.ffl?.business_name,
          ffl_phone_number: this.vendor?.ffl?.phone_number,
          ffl_email: this.vendor?.ffl?.email,
          notes: this.vendor?.ffl?.notes,
          expiration_date: this.vendor?.ffl?.expiration_date,
        });
      }
    }
    this.createDisplayedNameList();
    // Always map to string array for ng-select
    const rawOptions = this.sharedService.company_info.accounting_options.sales_tax_options;
    if (Array.isArray(rawOptions)) {
      this.sales_tax_options = rawOptions
        .map((option: any) => typeof option === 'string' ? option : option.name)
        .filter((v, i, a) => !!v && a.indexOf(v) === i);
    } else {
      this.sales_tax_options = [];
    }

    // Live-refresh the validation alert as the user edits. No-op until the
    // user has clicked Save at least once.
    this.add_edit_contact_form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshValidationMessage());
  }

  /** Compute the friendly names of every required field that isn't yet
   * satisfied. Skips labels for controls that don't exist on the form (so
   * the contact_type-specific labels naturally drop out). */
  private computeMissingFields(): string[] {
    const missing: string[] = [];
    for (const [control, label] of Object.entries(
      AddEditContactComponent.REQUIRED_FIELD_LABELS,
    )) {
      const ctrl = this.add_edit_contact_form.get(control);
      if (ctrl && ctrl.invalid) missing.push(label);
    }
    return missing;
  }

  /** Re-evaluate the alert as the user edits. No-op until the user has
   * clicked Save at least once. */
  refreshValidationMessage(): void {
    if (this.missing_fields.length === 0) return;
    this.missing_fields = this.computeMissingFields();
  }

  /** Click handler for the Save button. Button is visually disabled when
   * invalid but remains clickable so the user gets a chip-list explanation
   * of what's missing — same pattern as Sales / Expense. */
  attemptSave(): void {
    const missing = this.computeMissingFields();
    if (missing.length > 0) {
      this.add_edit_contact_form.markAllAsTouched();
      this.missing_fields = missing;
      return;
    }
    this.missing_fields = [];
    this.addEditContactClick();
  }
  addDynamicFormControls() {
    if (this.contact_type === ContactTypes.customer) {
      this.add_edit_contact_form.addControl(
        'tax_option',
        new FormControl({ value: null, disabled: false }, Validators.required),
      );
    } else if (this.contact_type === ContactTypes.vendor) {
      this.add_edit_contact_form.addControl(
        'ffl',
        new FormControl({ value: null, disabled: false }),
      );
      this.add_edit_contact_form.addControl(
        'legal_name',
        new FormControl({ value: null, disabled: false }),
      );
      this.add_edit_contact_form.addControl(
        'business_name',
        new FormControl({ value: null, disabled: false }),
      );
      this.add_edit_contact_form.addControl(
        'ffl_phone_number',
        new FormControl({ value: null, disabled: false }),
      );
      this.add_edit_contact_form.addControl(
        'ffl_email',
        new FormControl({ value: null, disabled: false }),
      );
      this.add_edit_contact_form.addControl(
        'notes',
        new FormControl({ value: null, disabled: false }),
      );
      this.add_edit_contact_form.addControl(
        'expiration_date',
        new FormControl({ value: null, disabled: true }),
      );
    }
  }
  closeModalClick(): void {
    this.add_edit_contact_form.reset();
    this.modalService.hide(ModalID.add_edit_contact);
    if (this.contact_type === ContactTypes.vendor) {
      this.accountingService.vendor_contact_modal_closed$.next(true);
    }
  }
  addEditContactClick() {
    if (this.modal_type !== ModalType.edit) {
      if (this.contact_type === ContactTypes.customer) {
        this.customer = new Customer();
        this.customer.contact_id = this.accountingService.createContactId(
          ContactTypes.customer,
        );
      } else {
        this.vendor = new Vendor();
        this.vendor.contact_id = this.accountingService.createContactId(
          ContactTypes.vendor,
        );
      }
    }
    if (this.contact_type === ContactTypes.customer) {
      this.addFfl = false;
      this.customer.contact.company_name =
        this.add_edit_contact_form.get('company_name').value;
      this.customer.contact.display_name =
        this.add_edit_contact_form.get('display_name').value;
      this.customer.contact.name_title =
        this.add_edit_contact_form.get('name_title').value;
      this.customer.contact.name_first =
        this.add_edit_contact_form.get('name_first').value;
      this.customer.contact.name_middle =
        this.add_edit_contact_form.get('name_middle').value;
      this.customer.contact.name_last =
        this.add_edit_contact_form.get('name_last').value;
      this.customer.contact.name_suffix =
        this.add_edit_contact_form.get('name_suffix').value;
      this.customer.contact.email =
        this.add_edit_contact_form.get('email').value;
      this.customer.contact.phone_number =
        this.add_edit_contact_form.get('phone_number').value;
      this.customer.contact.fax_number =
        this.add_edit_contact_form.get('fax_number').value;
      this.customer.contact.website =
        this.add_edit_contact_form.get('website').value;
      this.customer.contact.name_to_print_on_checks =
        this.add_edit_contact_form.get('name_to_print_on_checks').value;
      this.customer.contact.other =
        this.add_edit_contact_form.get('other').value;
      this.customer.address.street =
        this.add_edit_contact_form.get('street').value;
      this.customer.address.street2 =
        this.add_edit_contact_form.get('street2').value;
      this.customer.address.city = this.add_edit_contact_form.get('city').value;
      this.customer.address.state =
        this.add_edit_contact_form.get('state').value;
      this.customer.address.zip_code =
        this.add_edit_contact_form.get('zip_code').value;
      this.customer.address.country =
        this.add_edit_contact_form.get('country').value;
      const tax_option: salesTax =
        this.accountingService.sales_tax_options.filter(
          (tax_option) =>
            tax_option.name ===
            this.add_edit_contact_form.get('tax_option').value,
        )[0];
      this.customer.tax_option = tax_option;
      this.customer.memo = this.add_edit_contact_form.get('memo').value;
      this.customer.attachments = [];
    } else {
      this.vendor.contact.company_name =
        this.add_edit_contact_form.get('company_name').value;
      this.vendor.contact.display_name =
        this.add_edit_contact_form.get('display_name').value;
      this.vendor.contact.name_title =
        this.add_edit_contact_form.get('name_title').value;
      this.vendor.contact.name_first =
        this.add_edit_contact_form.get('name_first').value;
      this.vendor.contact.name_middle =
        this.add_edit_contact_form.get('name_middle').value;
      this.vendor.contact.name_last =
        this.add_edit_contact_form.get('name_last').value;
      this.vendor.contact.name_suffix =
        this.add_edit_contact_form.get('name_suffix').value;
      this.vendor.contact.email = this.add_edit_contact_form.get('email').value;
      this.vendor.contact.phone_number =
        this.add_edit_contact_form.get('phone_number').value;
      this.vendor.contact.fax_number =
        this.add_edit_contact_form.get('fax_number').value;
      this.vendor.contact.website =
        this.add_edit_contact_form.get('website').value;
      this.vendor.contact.name_to_print_on_checks =
        this.add_edit_contact_form.get('name_to_print_on_checks').value;
      this.vendor.contact.other = this.add_edit_contact_form.get('other').value;
      this.vendor.address.street =
        this.add_edit_contact_form.get('street').value;
      this.vendor.address.street2 =
        this.add_edit_contact_form.get('street2').value;
      this.vendor.address.city = this.add_edit_contact_form.get('city').value;
      this.vendor.address.state = this.add_edit_contact_form.get('state').value;
      this.vendor.address.zip_code =
        this.add_edit_contact_form.get('zip_code').value;
      this.vendor.address.country =
        this.add_edit_contact_form.get('country').value;
      this.vendor.memo = this.add_edit_contact_form.get('memo').value;
      this.vendor.attachments = [];
      this.vendor.ffl.ffl = this.add_edit_contact_form.get('ffl').value;
      this.vendor.ffl.legal_name =
        this.add_edit_contact_form.get('legal_name').value;
      this.vendor.ffl.business_name =
        this.add_edit_contact_form.get('business_name').value;
      this.vendor.ffl.phone_number =
        this.add_edit_contact_form.get('ffl_phone_number').value;
      this.vendor.ffl.email = this.add_edit_contact_form.get('ffl_email').value;
      this.vendor.ffl.notes = this.add_edit_contact_form.get('notes').value;
      this.vendor.ffl.expiration_date =
        this.add_edit_contact_form.get('expiration_date').value;
    }

    if (this.modal_type !== ModalType.edit) {
      if (this.contact_type === ContactTypes.customer) {
        this.accountingService.customer_contact_to_add$.next(this.customer);
      } else {
        console.warn(this.vendor);
        this.accountingService.vendor_contact_to_add$.next(this.vendor);
      }
    } else {
      if (this.contact_type === ContactTypes.customer) {
        const edit_customer_response_package: EditCustomerResponsePackage = {
          customer: this.customer,
          rowIndex: this.rowIndex,
        };
        this.accountingService.customer_contact_to_edit$.next(
          edit_customer_response_package,
        );
      } else {
        const edit_vendor_response_package: EditVendorResponsePackage = {
          vendor: this.vendor,
          rowIndex: this.rowIndex,
        };
        this.accountingService.vendor_contact_to_edit$.next(
          edit_vendor_response_package,
        );
      }
    }
    if (this.modal_type === ModalType.add && this.addFfl && this.vendor?.ffl?.ffl) {
      this.addFflToQuickSelection(this.vendor.ffl);
    }
    this.closeModalClick();
  }
  createDisplayedNameList() {
    let title = this.add_edit_contact_form.get('name_title').value
      ? this.add_edit_contact_form.get('name_title').value.toUpperCase()
      : '';
    let first = this.add_edit_contact_form.get('name_first').value
      ? this.add_edit_contact_form.get('name_first').value.toUpperCase()
      : '';
    let middle = this.add_edit_contact_form.get('name_middle').value
      ? this.add_edit_contact_form.get('name_middle').value.toUpperCase()
      : '';
    let last = this.add_edit_contact_form.get('name_last').value
      ? this.add_edit_contact_form.get('name_last').value.toUpperCase()
      : '';
    let suffix = this.add_edit_contact_form.get('name_suffix').value
      ? this.add_edit_contact_form.get('name_suffix').value.toUpperCase()
      : '';
    let company_name = this.add_edit_contact_form.get('company_name').value;
    let full_name = `${title} ${first} ${middle} ${last} ${suffix}`;
    this.customerDisplayNames = [];
    if (full_name) {
      this.customerDisplayNames.push(full_name);
    }
    if (company_name) {
      this.customerDisplayNames.push(company_name);
    }
    this.customerDisplayNames = [...this.customerDisplayNames];
  }

  findStateAndCity() {
    const zip = this.add_edit_contact_form.get('zip_code').value;
    if (zip?.length === 5) {
      this.sharedService.getCityState(zip).then((response) => {
        if (response) {
          this.distinct_cities = response.cities;
          this.add_edit_contact_form.patchValue({
            city: response.cities.length === 1 ? response.cities[0] : null,
            state: response.state,
            country: 'United States',
          });
        }
      });
    }
  }
  addFflToggleSwitch() {
    this.addFfl = !this.addFfl;
  }
  addFflToQuickSelection(new_ffl: FFL) {
    this.sharedService.addFFLToAvailabilityList(new_ffl);
  }
  verifyFFL() {
    const new_ffl = this.add_edit_contact_form.value.ffl.toUpperCase();
    if (
      new_ffl?.length === 20 &&
      this.add_edit_contact_form.get('expiration_date').value === null
    ) {
      if (FFLMonthCharacters.includes(new_ffl.substring(13, 14))) {
        for (const ffl of this.sharedService.ffl_list) {
          if (
            ffl.ffl.substring(0, 4) === new_ffl.substring(0, 4) &&
            ffl.ffl.substring(15, 20) === new_ffl.substring(15, 20)
          ) {
            this.add_edit_contact_form.patchValue({
              ffl: null,
              expiration_date: null,
            });
            this.sharedService.raiseWarningToast(
              'FFL already exists in the system.',
            );
            return;
          }
        }
        this.add_edit_contact_form.patchValue({
          expiration_date: calculateFFLExpirationDate(new_ffl),
          ffl_phone_number:
            this.add_edit_contact_form.get('phone_number').value,
          ffl_email: this.add_edit_contact_form.get('email').value,
        });
        if (!this.add_edit_contact_form.get('ffl_email').value) {
          this.add_edit_contact_form.patchValue({
            ffl_email: this.add_edit_contact_form.get('email').value,
          });
        }
        if (!this.add_edit_contact_form.get('ffl_phone_number').value) {
          this.add_edit_contact_form.patchValue({
            ffl_phone_number:
              this.add_edit_contact_form.get('phone_number').value,
          });
        }
        this.add_edit_contact_form
          .get('legal_name')
          .setValidators([Validators.required]);
        this.add_edit_contact_form.get('legal_name').updateValueAndValidity();
        this.add_edit_contact_form
          .get('ffl_email')
          .setValidators([Validators.required]);
        this.add_edit_contact_form.get('ffl_email').updateValueAndValidity();
      } else {
        this.sharedService.raiseErrorToast(
          'FFL Month is not valid. Please enter a valid FFL.',
        );
        this.add_edit_contact_form.patchValue({
          ffl: null,
          expiration_date: null,
        });
      }
    } else if (
      new_ffl?.length < 20 &&
      this.add_edit_contact_form.get('expiration_date').value !== null
    ) {
      this.add_edit_contact_form.patchValue({
        expiration_date: null,
      });
      this.add_edit_contact_form.get('legal_name').setValidators([]);
      this.add_edit_contact_form.get('legal_name').updateValueAndValidity();
      this.add_edit_contact_form.get('ffl_email').setValidators([]);
      this.add_edit_contact_form.get('ffl_email').updateValueAndValidity();
    }
  }
}
